import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useGroup } from '../../contexts/GroupContext';
import { useSavedViews } from '../../contexts/SavedViewsContext';
import { EntityIcon, SchemaIcon } from '../../components/common';
import { getEntityBg } from '../../constants/entityRegistry';
import { ShieldCheck, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../../services/api';
import { schemaLabel } from '../../utils/schemaUtils';
import { RealmFaces } from '../../components/SharedWithRow';
import { getPillColor } from '../../utils/pillColors';
import { useClickOutside } from '../../shared';
import './DatabaseTableView.css';
import { SwitchFace } from '../../components/nucleus/Switch';
import { ago } from '../../nucleus/format';

const COLLAPSED_LIMIT = 5;

// View types for the "Add a new view" picker. Only 'table' is live today; the
// rest are shown disabled ("soon") so the picker reads as a real roadmap.
const VIEW_TYPES = [
    { key: 'table', label: 'Table', icon: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M3 9h18M3 14.5h18M9 4v16" strokeLinecap="round"/></svg> },
    { key: 'board', label: 'Board', icon: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><rect x="3" y="4" width="5" height="16" rx="1.2"/><rect x="9.5" y="4" width="5" height="11" rx="1.2"/><rect x="16" y="4" width="5" height="14" rx="1.2"/></svg> },
    { key: 'gallery', label: 'Gallery', icon: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><rect x="3" y="3" width="7.5" height="7.5" rx="1.2"/><rect x="13.5" y="3" width="7.5" height="7.5" rx="1.2"/><rect x="3" y="13.5" width="7.5" height="7.5" rx="1.2"/><rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.2"/></svg> },
    { key: 'list', label: 'List', icon: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><path d="M8 6h13M8 12h13M8 18h13M3.5 6h.01M3.5 12h.01M3.5 18h.01"/></svg> },
    { key: 'calendar', label: 'Calendar', icon: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 9.5h18M8 3v4M16 3v4"/></svg> },
    { key: 'timeline', label: 'Timeline', icon: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><path d="M4 7h9M8 12h11M4 17h7"/></svg> },
    { key: 'chart', label: 'Chart', icon: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><circle cx="12" cy="12" r="9"/><path d="M12 3v9l6.5 3.5" strokeLinecap="round"/></svg> },
    { key: 'feed', label: 'Feed', icon: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M7 9h6M7 13h10"/></svg> },
    { key: 'map', label: 'Map', icon: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round"><path d="M9 4 3 6.5v13L9 17l6 2.5 6-2.5v-13L15 6.5 9 4z"/><path d="M9 4v13M15 6.5v13"/></svg> },
    { key: 'form', label: 'Form', icon: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><rect x="4" y="3" width="16" height="18" rx="2"/><path d="M8 8h8M8 12h8M8 16h4"/></svg> },
    { key: 'dashboard', label: 'Dashboard', icon: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><rect x="3" y="3" width="7.5" height="7.5" rx="1.2"/><rect x="13.5" y="3" width="7.5" height="7.5" rx="1.2"/><rect x="3" y="13.5" width="7.5" height="7.5" rx="1.2"/><rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.2"/></svg> },
];

/** Relative date for table cells, the date itself past a week; '—' for
 *  missing values (the table's convention). */
const fmtDateRelative = (dateStr) => ago(dateStr, { never: '—', dateAfter: 7 });

/**
 * Shared facet-item builder.  All facet lists (schemas, relations,
 * apps) follow the same pattern: map → compute counts → stamp _maxCount.
 */
function buildFacetItems(rawItems, countFn) {
    let maxCount = 0;
    const items = rawItems.map(raw => {
        const item = countFn(raw);
        if (item._count > maxCount) maxCount = item._count;
        return item;
    });
    items.forEach(i => { i._maxCount = maxCount; });
    return items;
}

function FilterBar({ label, count, maxCount, color, active, onClick, icon }) {
    const pct = maxCount > 0 ? (count / maxCount) * 100 : 0;
    return (
        <button className={`btn filter-bar ${active ? 'filter-bar--active' : ''}`} onClick={onClick} type="button">
            <span className="filter-bar__fill" style={{ width: `${pct}%`, backgroundColor: color || 'var(--primary-light)' }} />
            {icon && <span className="filter-bar__icon">{icon}</span>}
            <span className="filter-bar__label">{label}</span>
            <span className="filter-bar__count">{count}</span>
        </button>
    );
}

// Notion-style search: a magnifier icon that slides open into an inline field
// on click, and collapses back to the icon on blur when empty.
function CollapsibleSearch({ value, onChange }) {
    const [open, setOpen] = useState(!!value);
    const inputRef = useRef(null);
    useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);
    const expanded = open || !!value;
    return (
        <div className={`db-search ${expanded ? 'db-search--open' : ''}`} data-qh="table-search">
            <button
                type="button"
                className="btn db-search__icon"
                onClick={() => setOpen(true)}
                aria-label="Search"
                tabIndex={expanded ? -1 : 0}
            >
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.3"><circle cx="6.5" cy="6.5" r="4.5"/><path d="M10 10L13.5 13.5" strokeLinecap="round"/></svg>
            </button>
            <input
                ref={inputRef}
                type="text"
                className="db-search__input"
                placeholder="Search title & content…"
                value={value}
                onChange={e => onChange(e.target.value)}
                onBlur={() => { if (!value) setOpen(false); }}
                onKeyDown={e => { if (e.key === 'Escape') { onChange(''); setOpen(false); e.currentTarget.blur(); } }}
                tabIndex={expanded ? 0 : -1}
            />
            {value && (
                <button
                    type="button"
                    className="btn db-search__clear"
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => { onChange(''); inputRef.current?.focus(); }}
                    aria-label="Clear search"
                >&times;</button>
            )}
        </div>
    );
}

// Default column widths (px) and the floor a column can be dragged to. Widths
// are user-resizable and persisted to localStorage.
const DEFAULT_COL_WIDTHS = {
    title: 360, owner: 120, app: 150, schema: 190, relations: 180, stratum: 100, updated_at: 150, created_at: 150,
};
const MIN_COL_WIDTH = 64;
const COL_WIDTHS_KEY = 'db-col-widths';

// A column header that doubles as its filter trigger: clicking the title opens
// a popover anchored to the header. Keeps sort affordances for sortable columns.
function ColumnHeaderFilter({ icon, label, active, align, sortArrow, sortActive, children, onResizeStart, onHide }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const close = useCallback(() => setOpen(false), []);
    useClickOutside(ref, close, open);
    return (
        <th ref={ref} className={`db-th db-th--filter ${active ? 'db-th--filter-active' : ''} ${sortActive ? 'db-th--active' : ''}`}>
            <button type="button" className="btn db-th__trigger" onClick={() => setOpen(o => !o)} data-qh="col-header">
                <span className="db-th__icon">{icon}</span>
                {label}
                {active && <span className="db-th__active-dot" />}
                {sortArrow && <span className="db-sort-arrow">{sortArrow}</span>}
                <span className="db-th__chevron">{open ? '▴' : '▾'}</span>
            </button>
            {open && (
                <div className={`db-filter-popover db-th__popover ${align === 'right' ? 'db-th__popover--right' : ''}`}>
                    {children}
                    {onHide && (
                        <button type="button" className="btn db-th__hide" onClick={() => { onHide(); setOpen(false); }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" opacity="0.45"/><path d="M3 3l18 18"/></svg>
                            Hide property
                        </button>
                    )}
                </div>
            )}
            {onResizeStart && <span className="db-col-resize" data-qh="col-resize" onMouseDown={onResizeStart} onClick={e => e.stopPropagation()} />}
        </th>
    );
}

// A small gear button that jumps to a management page (e.g. Schemas)
// from inside a column filter dropdown.
function ManageCog({ onClick, label }) {
    return (
        <button type="button" className="btn db-filter-cog" onClick={onClick} title={label} aria-label={label}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
        </button>
    );
}

function FilterSection({ items, renderItem, searchable, title, searchAction }) {
    const [search, setSearch] = useState('');
    const [expanded, setExpanded] = useState(false);
    const filtered = useMemo(() => {
        if (!search.trim()) return items;
        const q = search.toLowerCase();
        return items.filter(i => (i._label || '').toLowerCase().includes(q));
    }, [items, search]);
    const isSearching = search.trim().length > 0;
    const visible = (expanded || isSearching) ? filtered : filtered.slice(0, COLLAPSED_LIMIT);
    const hiddenCount = filtered.length - COLLAPSED_LIMIT;

    return (
        <div className="db-filter-section">
            {((searchable && items.length > COLLAPSED_LIMIT) || searchAction) && (
                <div className="db-filter-search-row">
                    {searchable && items.length > COLLAPSED_LIMIT ? (
                        <input type="text" className="db-filter-search" placeholder={`Search ${title || ''}…`} value={search} onChange={e => { setSearch(e.target.value); setExpanded(false); }} />
                    ) : <span style={{ flex: 1 }} />}
                    {searchAction}
                </div>
            )}
            <div className="filter-bars">{visible.map(renderItem)}</div>
            {!isSearching && hiddenCount > 0 && (
                <button type="button" className="btn db-filter-more" onClick={() => setExpanded(!expanded)}>
                    {expanded ? 'Show less' : `+${hiddenCount} more…`}
                </button>
            )}
        </div>
    );
}

const MATCH_MODES = [
    { value: 'contains', label: 'contains' },
    { value: 'exact', label: 'is exactly' },
    { value: 'starts_with', label: 'starts with' },
];

function FieldSearchModal({ fields, schemaName, valueJsonField, setValueJsonField, valueJsonValue, setValueJsonValue, valueJsonMatchMode, setValueJsonMatchMode, onClose }) {
    const [fieldSearch, setFieldSearch] = useState('');
    const [draft, setDraft] = useState({ field: valueJsonField || '', value: valueJsonValue || '', match: valueJsonMatchMode || 'contains' });
    const inputRef = useRef(null);

    useEffect(() => { inputRef.current?.focus(); }, []);

    const filtered = useMemo(() => {
        const q = fieldSearch.toLowerCase();
        return q ? fields.filter(f => f.key.toLowerCase().includes(q)) : fields;
    }, [fields, fieldSearch]);

    const handleApply = () => {
        setValueJsonField(draft.field);
        setValueJsonValue(draft.value);
        setValueJsonMatchMode(draft.match);
        onClose();
    };

    const handleClear = () => {
        setValueJsonField('');
        setValueJsonValue('');
        setValueJsonMatchMode('contains');
        onClose();
    };

    const selectedField = fields.find(f => f.key === draft.field);

    return (
        <div className="fsm-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="fsm-modal" data-qh="field-search">
                <div className="fsm-header">
                    <span className="fsm-title">Search within <em>{schemaName}</em></span>
                    <button className="btn fsm-close" onClick={onClose}>×</button>
                </div>
                <div className="fsm-body">
                    <div className="fsm-left">
                        <input
                            ref={inputRef}
                            type="text"
                            className="fsm-field-search"
                            placeholder="Filter fields…"
                            value={fieldSearch}
                            onChange={e => setFieldSearch(e.target.value)}
                        />
                        <div className="fsm-field-list">
                            {filtered.map(f => (
                                <button
                                    key={f.key}
                                    className={`btn fsm-field-item ${draft.field === f.key ? 'fsm-field-item--active' : ''} ${f.encrypted ? 'fsm-field-item--encrypted' : ''}`}
                                    onClick={() => !f.encrypted && setDraft(d => ({ ...d, field: f.key, value: '' }))}
                                    title={f.encrypted ? 'Encrypted — cannot search' : undefined}
                                >
                                    <span className="fsm-field-name">{f.key}</span>
                                    <span className="fsm-field-type">{f.encrypted ? '🔒' : (f.type || 'any')}</span>
                                </button>
                            ))}
                            {filtered.length === 0 && <div className="fsm-empty">No fields match</div>}
                        </div>
                    </div>
                    <div className="fsm-right">
                        {draft.field ? (
                            <>
                                <div className="fsm-selected-label">Field: <strong>{draft.field}</strong>{selectedField?.type ? ` (${selectedField.type})` : ''}</div>
                                <div className="fsm-match-row">
                                    <select className="fsm-match-select" value={draft.match} onChange={e => setDraft(d => ({ ...d, match: e.target.value }))}>
                                        {MATCH_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                                    </select>
                                </div>
                                {selectedField?.enum ? (
                                    <select className="fsm-value-input" value={draft.value} onChange={e => setDraft(d => ({ ...d, value: e.target.value }))}>
                                        <option value="">— any —</option>
                                        {selectedField.enum.map(v => <option key={v} value={v}>{v}</option>)}
                                    </select>
                                ) : (
                                    <input
                                        type={selectedField?.type === 'number' && draft.match === 'exact' ? 'number' : 'text'}
                                        className="fsm-value-input"
                                        placeholder="Value…"
                                        value={draft.value}
                                        onChange={e => setDraft(d => ({ ...d, value: e.target.value }))}
                                        onKeyDown={e => { if (e.key === 'Enter') handleApply(); }}
                                        autoFocus={false}
                                    />
                                )}
                            </>
                        ) : (
                            <div className="fsm-prompt">Select a field on the left to search by its value.</div>
                        )}
                    </div>
                </div>
                <div className="fsm-footer">
                    <button className="btn fsm-btn fsm-btn--ghost" onClick={handleClear}>Clear</button>
                    <button className="btn fsm-btn fsm-btn--primary" onClick={handleApply} disabled={!draft.field || !draft.value}>Apply</button>
                </div>
            </div>
        </div>
    );
}

function DatabaseTableView({
    nodes, total, page, pageSize, onPageChange,
    sort, order, onSortChange, onOrderChange,
    selectedNodeId, pendingNodeId, onNodeSelect, schemasMap,
    // Filter props
    access, setAccess,
    graphSearch, setGraphSearch,
    schemaFilters, selectedSchemas, toggleSchema, deselectAllSchemas,
    relationships,
    relationshipTypes, selectedRelationshipTypes,
    toggleRelationshipType, deselectAllRelTypes,
    selectedApps, toggleApp, deselectAllApps,
    selectedStrata, toggleStratum, deselectAllStrata,
    facets,
    valueJsonField, setValueJsonField, valueJsonValue, setValueJsonValue,
    valueJsonMin, setValueJsonMin, valueJsonMax, setValueJsonMax,
    valueJsonMatchMode, setValueJsonMatchMode,
    dateField, setDateField, dateAfter, setDateAfter, dateBefore, setDateBefore,
    loading,
    onCreateNode,
    onManageSchemas,
    onManageApps,
    dbName = null,
    catalogMode = false,
}) {
    const { isGroupContext } = useGroup();
    const { views, activeViewId, applyView, createView, renameView, deleteView, updateViewSettings } = useSavedViews();
    const [showNewView, setShowNewView] = useState(false);
    const [newViewName, setNewViewName] = useState('');
    const newViewRef = useRef(null);
    const [menuViewId, setMenuViewId] = useState(null);   // which view's dropdown is open
    const [editingViewId, setEditingViewId] = useState(null); // which view is inline-renaming
    const [editName, setEditName] = useState('');
    const [editPanelViewId, setEditPanelViewId] = useState(null); // which view's "Edit view" panel is open
    const [panelName, setPanelName] = useState('');
    const [settingsOpen, setSettingsOpen] = useState(false); // right-side Settings panel for the active database
    const editPanelRef = useRef(null);
    const settingsBtnRef = useRef(null);
    const viewTabsRef = useRef(null);
    const [showFieldModal, setShowFieldModal] = useState(false);

    // Resizable columns — widths persisted to localStorage, merged over defaults
    // so newly-added columns pick up their default.
    const [colWidths, setColWidths] = useState(() => {
        try { return { ...DEFAULT_COL_WIDTHS, ...JSON.parse(localStorage.getItem(COL_WIDTHS_KEY) || '{}') }; }
        catch { return { ...DEFAULT_COL_WIDTHS }; }
    });
    useEffect(() => {
        try { localStorage.setItem(COL_WIDTHS_KEY, JSON.stringify(colWidths)); } catch { /* ignore */ }
    }, [colWidths]);

    // Wrap-cell text vs. truncate. Per-saved-view (lives in the view's settings,
    // edited from its Edit panel); the default "All" view falls back to a global
    // flag since it has no Edit panel of its own.
    const [defaultWrap, setDefaultWrap] = useState(() => {
        try { return localStorage.getItem('db-wrap-text') === '1'; } catch { return false; }
    });
    const activeView = views.find(v => v.id === activeViewId) || null;
    // The custom database we're currently inside — either because its own (default)
    // view is active, or because one of its child views is. Custom databases own
    // multiple views Notion-style; child views carry settings.parentDatabaseId.
    const activeDatabase = activeView?.settings?.clusterTagId
        ? activeView
        : (activeView?.settings?.parentDatabaseId ? (views.find(v => v.id === activeView.settings.parentDatabaseId) || null) : null);
    const childViews = activeDatabase ? views.filter(v => v.settings?.parentDatabaseId === activeDatabase.id) : [];
    const wrapText = activeViewId && activeView ? !!activeView.settings?.wrapText : defaultWrap;
    // Wrap toggle that works for the active database — a saved view, or the
    // master library (global default) when none is active.
    const setWrapText = (next) => {
        if (activeViewId && activeView) updateViewSettings(activeViewId, { wrapText: next });
        else { setDefaultWrap(next); try { localStorage.setItem('db-wrap-text', next ? '1' : '0'); } catch { /* ignore */ } }
    };

    // Hidden columns — same per-view model as wrap. The Name/title column is the
    // primary and can't be hidden (Notion-style).
    const [globalHidden, setGlobalHidden] = useState(() => {
        try { return JSON.parse(localStorage.getItem('db-hidden-cols') || '[]'); } catch { return []; }
    });
    useEffect(() => {
        try { localStorage.setItem('db-hidden-cols', JSON.stringify(globalHidden)); } catch { /* ignore */ }
    }, [globalHidden]);
    const hiddenCols = activeViewId && activeView ? (activeView.settings?.hiddenColumns || []) : globalHidden;
    const isColVisible = (key) => key === 'title' || !hiddenCols.includes(key);
    const setHiddenCols = (next) => {
        if (activeViewId && activeView) updateViewSettings(activeViewId, { hiddenColumns: next });
        else setGlobalHidden(next);
    };
    const hideColumn = (key) => { if (key !== 'title' && !hiddenCols.includes(key)) setHiddenCols([...hiddenCols, key]); };

    const startColResize = (key, e) => {
        e.preventDefault();
        e.stopPropagation();
        const startX = e.clientX;
        const startW = colWidths[key] ?? DEFAULT_COL_WIDTHS[key] ?? 150;
        const onMove = (ev) => {
            const w = Math.max(MIN_COL_WIDTH, startW + (ev.clientX - startX));
            setColWidths(prev => ({ ...prev, [key]: w }));
        };
        const onUp = () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            document.body.classList.remove('db-col-resizing');
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
        document.body.classList.add('db-col-resizing');
    };

    // Click-outside: new-view popover
    const closeNewView = useCallback(() => setShowNewView(false), []);
    useClickOutside(newViewRef, closeNewView, showNewView);

    // Click-outside: view tab menu
    const closeMenuView = useCallback(() => setMenuViewId(null), []);
    useClickOutside(viewTabsRef, closeMenuView, !!menuViewId);

    // Click-outside: edit panel (must be outside both panel AND view tabs)
    useEffect(() => {
        if (!editPanelViewId) return;
        const handler = (e) => {
            if (editPanelRef.current && !editPanelRef.current.contains(e.target)
                && viewTabsRef.current && !viewTabsRef.current.contains(e.target)) {
                setEditPanelViewId(null);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [editPanelViewId]);

    const editPanelView = views.find(v => v.id === editPanelViewId) || null;
    useEffect(() => { if (editPanelView) setPanelName(editPanelView.name); }, [editPanelViewId, editPanelView]);

    // Click-outside: settings panel (must be outside both panel AND toggle button)
    useEffect(() => {
        if (!settingsOpen) return;
        const handler = (e) => {
            if (editPanelRef.current && !editPanelRef.current.contains(e.target)
                && settingsBtnRef.current && !settingsBtnRef.current.contains(e.target)) {
                setSettingsOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [settingsOpen]);
    // Keep the rename field in sync with the active database's name while open.
    useEffect(() => { if (settingsOpen && activeView) setPanelName(activeView.name); }, [settingsOpen, activeViewId, activeView]);

    const handleCreateView = () => {
        const name = newViewName.trim() || 'New view';
        if (activeDatabase) {
            // A view within the active database — starts as a copy of the database's
            // filter (same nodes), narrowable further, kept scoped via parentDatabaseId.
            createView(name, { ...(activeDatabase.filters || {}) }, { parentDatabaseId: activeDatabase.id });
        } else {
            createView(name);
        }
        setNewViewName('');
        setShowNewView(false);
    };

    const commitRename = (id) => {
        const n = editName.trim();
        if (n) renameView(id, n);
        setEditingViewId(null);
        setEditName('');
    };
    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    // Resolve pending node from URL (don't auto-select otherwise)
    useEffect(() => {
        if (pendingNodeId && !selectedNodeId && nodes.length > 0) {
            const found = nodes.find(n => n.id === pendingNodeId);
            if (found) onNodeSelect?.(found);
        }
    }, [pendingNodeId, selectedNodeId, nodes, onNodeSelect]);

    // Field search schema detail
    const [fieldSchemaDetail, setFieldSchemaDetail] = useState(null);
    const singleSchemaId = selectedSchemas.size === 1 ? [...selectedSchemas][0] : null;
    useEffect(() => {
        if (!singleSchemaId || singleSchemaId === '__none__') { setFieldSchemaDetail(null); return; }
        api.request(`/schemas/${singleSchemaId}?include_json_schema=true`)
            .then(s => setFieldSchemaDetail(s)).catch(() => setFieldSchemaDetail(null));
    }, [singleSchemaId]);

    const filterableFields = useMemo(() => {
        if (!fieldSchemaDetail?.json_schema?.properties) return [];
        // Include formerly encrypted fields — the server refuses searches on
        // them too (legacy ciphertext may still be stored under them).
        const encrypted = new Set([
            ...(fieldSchemaDetail.encrypted_fields || []),
            ...(fieldSchemaDetail.formerly_encrypted_fields || []),
        ]);
        return Object.keys(fieldSchemaDetail.json_schema.properties).map(key => {
            const def = fieldSchemaDetail.json_schema.properties[key];
            return { key, encrypted: encrypted.has(key), type: def.type, format: def.format, enum: def.enum };
        });
    }, [fieldSchemaDetail]);

    // Computed filter items — use server-side facets when available, fall back to current-page nodes
    const schemaItems = useMemo(() =>
        buildFacetItems(schemaFilters || [], schema => {
            const c = facets ? (facets.schemas?.[schema.id] || 0) : (nodes || []).filter(n => schema.id === '__none__' ? !n.schema_id : n.schema_id === schema.id).length;
            const uri = schemasMap?.get(schema.id)?.schema_name;
            return { ...schema, _count: c, _label: uri ? schemaLabel(uri) : (schema.name || schema.id) };
        }),
    [schemaFilters, facets, nodes, schemasMap]);

    // Client-side join: map each node id -> the distinct relationship types it
    // participates in (as source or target). Built from the already-loaded full
    // edge list, so the Relations column needs no extra fetch.
    const relsByNode = useMemo(() => {
        const m = new Map();
        (relationships || []).forEach(r => {
            const s = r.source?.id || r.source || r.from_node_id || r.source_node_id;
            const t = r.target?.id || r.target || r.to_node_id || r.target_node_id;
            const type = r.relationship_type || 'related';
            if (s) { if (!m.has(s)) m.set(s, new Set()); m.get(s).add(type); }
            if (t) { if (!m.has(t)) m.set(t, new Set()); m.get(t).add(type); }
        });
        return m;
    }, [relationships]);

    const relItems = useMemo(() => {
        const relCounts = facets?.relationships || {};
        return buildFacetItems(relationshipTypes || [], type => ({
            _type: type, _count: relCounts[type] || 0, _label: type,
        }));
    }, [relationshipTypes, facets]);

    // App filter items
    const appItems = useMemo(() => {
        let appCounts;
        if (facets) {
            appCounts = facets.apps || {};
        } else {
            appCounts = {};
            (nodes || []).forEach(n => {
                const app = n.created_by_app_name || 'User created';
                appCounts[app] = (appCounts[app] || 0) + 1;
            });
        }
        const items = buildFacetItems(Object.entries(appCounts), ([name, count]) => ({
            _app: name, _count: count, _label: name,
        }));
        items.sort((a, b) => b._count - a._count);
        return items;
    }, [facets, nodes]);

    const hasDateFilter = !!(dateAfter || dateBefore);
    const hasAnyFilter = selectedSchemas.size > 0 || selectedRelationshipTypes.size > 0 || graphSearch || valueJsonField || selectedApps.size > 0 || (selectedStrata && selectedStrata.size > 0) || hasDateFilter;

    const resetAll = () => {
        deselectAllSchemas(); deselectAllRelTypes(); deselectAllApps();
        deselectAllStrata?.();
        setGraphSearch('');
        setValueJsonField?.(''); setValueJsonValue?.(''); setValueJsonMin?.(''); setValueJsonMax?.('');
        setDateAfter?.(''); setDateBefore?.(''); setDateField?.('created_at');
        setAccess('owned');
    };

    /* Who can reach each node on this page, in one request.
       Per-row calls would be 25 round trips to paint a column, and computing
       reach in JavaScript would put the rule that decides who can see your data
       in a second place — which is the copy that goes quietly wrong. */
    const [sharedBy, setSharedBy] = useState({});
    /* Depends on PRIMITIVES, not on isColVisible.
       isColVisible is redefined every render, so listing it here made the deps
       change on every render: effect → setSharedBy → render → effect, until
       React gave up and the whole page fell to the error boundary. The id list
       is joined for the same reason — a fresh array each render is a fresh
       dependency. */
    const sharedColOn = isColVisible('shared');
    const nodeIdsKey = (nodes || []).map(n => n.id).filter(Boolean).join(',');
    useEffect(() => {
        const ids = nodeIdsKey ? nodeIdsKey.split(',') : [];
        if (!ids.length || !sharedColOn) { setSharedBy({}); return; }
        let cancelled = false;
        api.request('/nodes/shared-with', { method: 'POST', body: { node_ids: ids } })
            .then(r => { if (!cancelled) setSharedBy(r?.grouped || {}); })
            .catch(() => { if (!cancelled) setSharedBy({}); });
        return () => { cancelled = true; };
    }, [nodeIdsKey, sharedColOn]);

    const columns = [
        { key: 'title', label: 'Name', sortKey: 'title', icon: <span style={{ fontSize: 11, fontWeight: 600, opacity: 0.7, lineHeight: 1 }}>Aa</span> },
        { key: 'owner', label: 'Ownership', sortKey: null, icon: <svg width="12" height="12" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.3"><circle cx="7.5" cy="4.5" r="2.5"/><path d="M2 13c0-3 2.5-4.5 5.5-4.5S13 10 13 13" strokeLinecap="round"/></svg> },
        { key: 'shared', label: 'Shared', sortKey: null, icon: <svg width="12" height="12" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.3"><circle cx="5.5" cy="4.5" r="2.2"/><path d="M1.5 12.5c0-2.2 1.8-3.7 4-3.7s4 1.5 4 3.7" strokeLinecap="round"/><path d="M10 6.6a2.2 2.2 0 0 0 0-4.2M12 12.5c0-1.5-.6-2.7-1.7-3.4" strokeLinecap="round"/></svg> },
        { key: 'app', label: 'App', sortKey: null, icon: <svg width="12" height="12" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.3"><rect x="1.5" y="1.5" width="4.5" height="4.5" rx="1"/><rect x="9" y="1.5" width="4.5" height="4.5" rx="1"/><rect x="1.5" y="9" width="4.5" height="4.5" rx="1"/><rect x="9" y="9" width="4.5" height="4.5" rx="1"/></svg> },
        { key: 'schema', label: 'Schema', sortKey: null, icon: <svg width="12" height="12" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.3"><circle cx="7.5" cy="7.5" r="5.5"/><path d="M2.5 5.5h10M2.5 9.5h10M7.5 2v11" strokeLinecap="round"/></svg> },
        { key: 'relations', label: 'Relations', sortKey: null, icon: <svg width="12" height="12" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.3"><circle cx="3.5" cy="3.5" r="2"/><circle cx="11.5" cy="11.5" r="2"/><circle cx="11.5" cy="3.5" r="2"/><path d="M5.5 3.5h4M3.5 5.5v4a2 2 0 0 0 2 2h4" strokeLinecap="round"/></svg> },
        { key: 'stratum', label: 'Stratum', sortKey: null, icon: <svg width="12" height="12" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.3"><path d="M7.5 1.5 1.5 4.5l6 3 6-3-6-3z"/><path d="M1.5 7.5l6 3 6-3"/><path d="M1.5 10.5l6 3 6-3"/></svg> },
        { key: 'updated_at', label: 'Last updated', sortKey: 'updated_at', icon: <svg width="12" height="12" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.3"><circle cx="7.5" cy="7.5" r="5.5"/><path d="M7.5 4.5V7.5L9.5 9.5" strokeLinecap="round" strokeLinejoin="round"/></svg> },
        { key: 'created_at', label: 'Created', sortKey: 'created_at', icon: <svg width="12" height="12" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.3"><rect x="2" y="3" width="11" height="10" rx="1.5"/><path d="M5 1.5v3M10 1.5v3M2 6.5h11" strokeLinecap="round"/></svg> },
    ];
    const visibleColumns = columns.filter(c => isColVisible(c.key));

    const handleColumnSort = (sortKey) => {
        if (!sortKey) return;
        if (sort === sortKey) {
            onOrderChange(order === 'desc' ? 'asc' : 'desc');
        } else {
            onSortChange(sortKey);
            onOrderChange('desc');
        }
    };

    // Date filter scoped to a single field (created_at / updated_at) — each date
    // column header opens its own. After/Before share one date-field at a time
    // (data layer supports one), so a header is only "active" when it owns it.
    const dateFilterContent = (field) => {
        const af = dateField === field ? (dateAfter || '') : '';
        const bf = dateField === field ? (dateBefore || '') : '';
        return (
            <div style={{ padding: '8px 10px', minWidth: 220 }}>
                <div className="db-date-toggle">
                    <button className={`btn db-source-btn ${sort === field && order === 'desc' ? 'db-source-btn--active' : ''}`} onClick={() => { onSortChange(field); onOrderChange('desc'); }}>Newest</button>
                    <button className={`btn db-source-btn ${sort === field && order === 'asc' ? 'db-source-btn--active' : ''}`} onClick={() => { onSortChange(field); onOrderChange('asc'); }}>Oldest</button>
                </div>
                <label className="db-date-label">After</label>
                <input type="date" className="db-date-input" value={af} onChange={e => { setDateField?.(field); setDateAfter?.(e.target.value); }} />
                <label className="db-date-label">Before</label>
                <input type="date" className="db-date-input" value={bf} onChange={e => { setDateField?.(field); setDateBefore?.(e.target.value); }} />
                {(af || bf) && <button className="btn db-filter-remove-btn" onClick={() => { setDateAfter?.(''); setDateBefore?.(''); }}>Clear dates</button>}
            </div>
        );
    };

    // Maps a column key -> the filter that lives in that column's header.
    const columnFilters = {
        app: {
            active: selectedApps.size > 0,
            align: 'left',
            content: (
                <>
                    <FilterSection title="apps" items={appItems} searchable
                        searchAction={onManageApps && <ManageCog onClick={onManageApps} label="Manage applications" />}
                        renderItem={item => (
                        <FilterBar key={item._app} label={item._app} count={item._count} maxCount={item._maxCount} active={selectedApps.has(item._app)} onClick={() => toggleApp(item._app)} />
                    )} />
                    {selectedApps.size > 0 && <button className="btn db-filter-remove-btn" onClick={deselectAllApps}>Clear</button>}
                </>
            ),
        },
        updated_at: { active: hasDateFilter && dateField === 'updated_at', align: 'right', content: dateFilterContent('updated_at') },
        created_at: { active: hasDateFilter && dateField === 'created_at', align: 'right', content: dateFilterContent('created_at') },
    };
    if ((schemaFilters || []).length > 0) {
        columnFilters.schema = {
            active: selectedSchemas.size > 0,
            align: 'left',
            content: (
                <>
                    <FilterSection title="schemas" items={schemaItems} searchable
                        searchAction={onManageSchemas && <ManageCog onClick={onManageSchemas} label="Manage schemas" />}
                        renderItem={schema => {
                        const full = schemasMap?.get(schema.id);
                        const iconName = full?.schema_metadata?.icon;
                        return (
                            <FilterBar key={schema.id} label={schema._label} count={schema._count} maxCount={schema._maxCount}
                                color={schema.id === '__none__' ? 'rgba(148,163,184,0.15)' : getEntityBg('schema')}
                                active={selectedSchemas.has(schema.id)} onClick={() => toggleSchema(schema.id)}
                                icon={schema.id !== '__none__' ? <SchemaIcon iconName={iconName} size={12} /> : null} />
                        );
                    }} />
                    {selectedSchemas.size > 0 && <button className="btn db-filter-remove-btn" onClick={deselectAllSchemas}>Clear</button>}
                </>
            ),
        };
    }
    if ((relationshipTypes || []).length > 0) {
        columnFilters.relations = {
            active: selectedRelationshipTypes.size > 0,
            align: 'left',
            content: (
                <>
                    <FilterSection title="relationships" items={relItems} searchable renderItem={item => (
                        <FilterBar key={item._type} label={item._type} count={item._count} maxCount={item._maxCount} color={getPillColor(item._type).bg} active={selectedRelationshipTypes.has(item._type)} onClick={() => toggleRelationshipType(item._type)} />
                    )} />
                    {selectedRelationshipTypes.size > 0 && <button className="btn db-filter-remove-btn" onClick={deselectAllRelTypes}>Clear</button>}
                </>
            ),
        };
    }
    if (!isGroupContext && !catalogMode) {
        const oc = facets?.ownership || {};
        const ownershipItems = [
            { value: 'owned', label: 'Mine', _count: oc.owned ?? 0 },
            { value: 'shared', label: 'Shared', _count: oc.shared ?? 0 },
            { value: 'both', label: 'All', _count: oc.all ?? 0 },
        ];
        const maxOwnership = Math.max(...ownershipItems.map(i => i._count), 1);
        columnFilters.owner = {
            active: access !== 'owned',
            align: 'left',
            content: (
                <div className="db-filter-section">
                    <div className="filter-bars">
                        {ownershipItems.map(opt => (
                            <FilterBar key={opt.value} label={opt.label} count={opt._count} maxCount={maxOwnership} active={access === opt.value} onClick={() => setAccess(opt.value)} />
                        ))}
                    </div>
                </div>
            ),
        };
    }

    if (selectedStrata && toggleStratum) {
        const STRATA = ['raw', 'inferred', 'declared'];
        columnFilters.stratum = {
            active: selectedStrata.size > 0,
            align: 'left',
            content: (
                <div className="db-filter-section">
                    <div className="filter-bars">
                        {STRATA.map(s => (
                            <FilterBar key={s} label={s.charAt(0).toUpperCase() + s.slice(1)} count={0} maxCount={1} active={selectedStrata.has(s)} onClick={() => toggleStratum(s)} />
                        ))}
                    </div>
                    {selectedStrata.size > 0 && <button className="btn db-filter-remove-btn" onClick={deselectAllStrata}>Clear</button>}
                </div>
            ),
        };
    }

    // Per-row derived data — cache schema lookups and pill colors once per node
    // to avoid redundant calls in the render body.
    const rowMeta = useMemo(() => {
        const map = new Map();
        (nodes || []).forEach(node => {
            const schema = node.schema_id ? schemasMap?.get(node.schema_id) : null;
            const schemaName = schema ? (schemaLabel(schema.schema_name) || null) : null;
            const schemaIcon = schema?.schema_metadata?.icon || null;
            const isValidated = schema?.is_system || false;
            const appPill = node.created_by_app_name ? getPillColor(node.created_by_app_name) : null;
            const schemaPill = schemaName ? getPillColor(schemaName) : null;
            map.set(node.id, { schemaName, schemaIcon, isValidated, appPill, schemaPill });
        });
        return map;
    }, [nodes, schemasMap]);

    return (
        <div className="db-table-view ofself-page-fade">
            {/* Database title — only when the view has a name of its own; the
                master node library shows none. */}
            {dbName && <h1 className="db-title" data-qh="table-title">{dbName}</h1>}

            {/* Top header row — view tabs (left) + search/New + filters (right) */}
            <div className="db-header-row">
            {/* View tabs — scoped to the active custom database (its own default view
                plus any child views). The master node library shows no tabs; databases
                are navigated from the left sidebar. */}
            {!isGroupContext && !catalogMode && activeDatabase && (
                <div className="db-views" ref={viewTabsRef} data-qh="table-view-tab">
                    <button
                        type="button"
                        className={`btn db-view-tab ${activeViewId === activeDatabase.id ? 'active' : ''}`}
                        onClick={() => applyView(activeDatabase)}
                    >
                        <svg className="db-view-tab__icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M3 15h18M9 3v18" strokeLinecap="round"/></svg>
                        Table
                    </button>
                    {childViews.map(v => {
                        const isActive = activeViewId === v.id;
                        const isEditing = editingViewId === v.id;
                        return (
                            <div key={v.id} className={`db-view-tab-wrap ${isActive ? 'active' : ''}`}>
                                {isEditing ? (
                                    <input
                                        className="db-view-tab__edit"
                                        autoFocus
                                        value={editName}
                                        onChange={e => setEditName(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Enter') commitRename(v.id); if (e.key === 'Escape') { setEditingViewId(null); setEditName(''); } }}
                                        onBlur={() => commitRename(v.id)}
                                    />
                                ) : (
                                    <button
                                        type="button"
                                        className={`btn db-view-tab ${isActive ? 'active' : ''}`}
                                        title={v.name}
                                        onClick={() => { if (isActive) setMenuViewId(m => (m === v.id ? null : v.id)); else { applyView(v); setMenuViewId(null); } }}
                                    >
                                        {v.name}
                                    </button>
                                )}
                                {menuViewId === v.id && (
                                    <div className="db-view-menu">
                                        <button type="button" className="btn db-view-menu__item" onClick={() => { setEditingViewId(v.id); setEditName(v.name); setMenuViewId(null); }}>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z"/></svg>
                                            Rename
                                        </button>
                                        <button type="button" className="btn db-view-menu__item" onClick={() => { applyView(v); setSettingsOpen(true); setMenuViewId(null); }}>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><line x1="4" y1="8" x2="20" y2="8"/><line x1="4" y1="16" x2="20" y2="16"/><circle cx="9" cy="8" r="2"/><circle cx="15" cy="16" r="2"/></svg>
                                            Edit view
                                        </button>
                                        <button type="button" className="btn db-view-menu__item db-view-menu__item--danger" onClick={() => { setMenuViewId(null); if (activeViewId === v.id) applyView(activeDatabase); deleteView(v.id); }}>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14"/></svg>
                                            Delete view
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    <div className="db-view-add-wrap" ref={newViewRef}>
                        <button
                            type="button"
                            className="btn db-view-add"
                            data-qh="table-add-view"
                            title="Add a new view"
                            onClick={() => setShowNewView(v => !v)}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                        </button>
                        {showNewView && (
                            <div className="db-view-popover">
                                <input
                                    className="db-view-popover__name"
                                    autoFocus
                                    placeholder="View name"
                                    value={newViewName}
                                    onChange={e => setNewViewName(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') handleCreateView(); if (e.key === 'Escape') setShowNewView(false); }}
                                />
                                <div className="db-view-popover__hint">Add a new view</div>
                                <div className="db-view-popover__grid">
                                    {VIEW_TYPES.map(t => {
                                        const live = t.key === 'table';
                                        return (
                                            <button
                                                key={t.key}
                                                type="button"
                                                disabled={!live}
                                                className={`btn db-view-type ${live ? 'db-view-type--live' : 'db-view-type--soon'}`}
                                                title={live ? 'Table' : 'Coming soon'}
                                                onClick={() => { if (live) handleCreateView(); }}
                                            >
                                                <span className="db-view-type__icon">{t.icon}</span>
                                                <span className="db-view-type__label">{t.label}</span>
                                                {!live && <span className="db-view-type__soon">soon</span>}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

                {/* Controls — pushed to the right, aligned with the view tabs row */}
                <div className="db-header-controls">
                    {/* Ownership / App / Schema / Date filters live in their column headers */}
                    {singleSchemaId && singleSchemaId !== '__none__' && filterableFields.length > 0 && (
                        <button
                            className={`btn db-filter-trigger ${valueJsonField ? 'db-filter-trigger--active' : ''}`}
                            data-qh="field-search"
                            onClick={() => setShowFieldModal(true)}
                        >
                            {valueJsonField ? `Field: ${valueJsonField}` : 'Fields'}
                            {valueJsonField ? (
                                <span className="db-filter-clear" onClick={e => { e.stopPropagation(); setValueJsonField?.(''); setValueJsonValue?.(''); setValueJsonMatchMode?.('contains'); }} title="Clear">×</span>
                            ) : (
                                <span className="db-filter-chevron">⊕</span>
                            )}
                        </button>
                    )}
                    {hasAnyFilter && <button className="btn db-reset-btn" data-qh="table-reset" onClick={resetAll}>Reset</button>}
                    {!isGroupContext && !catalogMode && (
                        <button
                            ref={settingsBtnRef}
                            type="button"
                            className={`btn db-settings-btn ${settingsOpen ? 'db-settings-btn--active' : ''}`}
                            data-qh="table-settings"
                            onClick={() => setSettingsOpen(o => !o)}
                            title="Settings"
                            aria-label="Settings"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><line x1="4" y1="8" x2="20" y2="8"/><line x1="4" y1="16" x2="20" y2="16"/><circle cx="9" cy="8" r="2.2"/><circle cx="15" cy="16" r="2.2"/></svg>
                        </button>
                    )}
                    {/* SEARCH IS THE RIGHTMOST SEAT (owner call 2026-08-22) —
                        the one ordering every pane bar speaks. */}
                    <CollapsibleSearch value={graphSearch} onChange={setGraphSearch} />
                    {/* No "+ New" in the chrome (owner call 2026-08-21):
                        creation lives IN the content — the blocks grid's
                        perpetual first tile, the table's standing New row. */}
                </div>
            </div>{/* end db-header-row */}

            {/* Field search modal */}
            {showFieldModal && (
                <FieldSearchModal
                    fields={filterableFields}
                    schemaName={schemaLabel(fieldSchemaDetail?.schema_name) || schemaLabel(schemasMap?.get(singleSchemaId)?.schema_name) || 'schema'}
                    valueJsonField={valueJsonField}
                    setValueJsonField={setValueJsonField}
                    valueJsonValue={valueJsonValue}
                    setValueJsonValue={setValueJsonValue}
                    valueJsonMatchMode={valueJsonMatchMode}
                    setValueJsonMatchMode={setValueJsonMatchMode}
                    onClose={() => setShowFieldModal(false)}
                />
            )}

            {/* Table */}
            <div className="db-table-wrap">
                {loading ? (
                    <div className="db-loading"><div className="spinner" /><p>Loading…</p></div>
                ) : (
                    <table className={`db-table ${wrapText ? 'db-table--wrap' : ''}`}
                        style={{ width: visibleColumns.reduce((s, c) => s + (colWidths[c.key] || DEFAULT_COL_WIDTHS[c.key] || 150), 0), minWidth: '100%' }}>
                        <colgroup>
                            {visibleColumns.map(col => (
                                <col key={col.key} style={{ width: colWidths[col.key] || DEFAULT_COL_WIDTHS[col.key] || 150 }} />
                            ))}
                        </colgroup>
                        <thead>
                            <tr>
                                {visibleColumns.map(col => {
                                    const cf = columnFilters[col.key];
                                    if (cf) {
                                        return (
                                            <ColumnHeaderFilter key={col.key} icon={col.icon} label={col.label}
                                                active={cf.active} align={cf.align}
                                                sortArrow={col.sortKey && sort === col.sortKey ? (order === 'desc' ? '↓' : '↑') : null}
                                                sortActive={!!col.sortKey && sort === col.sortKey}
                                                onResizeStart={(e) => startColResize(col.key, e)}
                                                onHide={() => hideColumn(col.key)}>
                                                {cf.content}
                                            </ColumnHeaderFilter>
                                        );
                                    }
                                    return (
                                        <th key={col.key} className={`db-th ${col.sortKey ? 'db-th--sortable' : ''} ${sort === col.sortKey ? 'db-th--active' : ''}`}
                                            onClick={() => handleColumnSort(col.sortKey)}>
                                            <span className="db-th__icon">{col.icon}</span>
                                            {col.label}
                                            {sort === col.sortKey && <span className="db-sort-arrow">{order === 'desc' ? ' ↓' : ' ↑'}</span>}
                                            <span className="db-col-resize" data-qh="col-resize" onMouseDown={(e) => startColResize(col.key, e)} onClick={e => e.stopPropagation()} />
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {nodes.map(node => {
                                const meta = rowMeta.get(node.id) || {};
                                return (
                                <tr key={node.id}
                                    className={`db-row ${selectedNodeId === node.id ? 'db-row--selected' : ''}`}
                                    draggable={!isGroupContext && !catalogMode}
                                    onDragStart={(e) => {
                                        e.dataTransfer.setData('application/x-node', JSON.stringify({ id: node.id }));
                                        e.dataTransfer.setData('text/plain', String(node.id));
                                        e.dataTransfer.effectAllowed = 'copy';
                                    }}
                                    onClick={() => onNodeSelect?.(node)}>
                                    <td className="db-td db-td--name" data-qh="cell-title">
                                        <span className="db-name-inner">
                                            <EntityIcon type="node" />
                                            <span className="db-node-title">{node.title || node.id?.slice(0, 12)}</span>
                                        </span>
                                    </td>
                                    {isColVisible('owner') && (
                                        <td className="db-td db-td--owner" data-qh="prop-owner">
                                            {node.owner?.username ? (
                                                <span className="db-owner-pill">{node.owner.username}</span>
                                            ) : (!isGroupContext && !catalogMode && access === 'owned') ? (
                                                <span className="db-owner-pill db-owner-pill--self">You</span>
                                            ) : null}
                                        </td>
                                    )}
                                    {isColVisible('shared') && (
                                        <td className="db-td db-td--shared" data-qh="prop-shared">
                                            {(() => {
                                                /* Realms, not the people in them — a person reaches
                                                   the node because a realm grants it to them, so a
                                                   face for each said the same grant twice. */
                                                const realms = sharedBy[node.id] || [];
                                                if (!realms.length) {
                                                    /* A word, not a blank. An empty cell reads as
                                                       data that failed to load. */
                                                    return <span className="db-shared-private">Private</span>;
                                                }
                                                return (
                                                    <span className="db-shared">
                                                        <RealmFaces groups={realms} max={3} />
                                                    </span>
                                                );
                                            })()}
                                        </td>
                                    )}
                                    {isColVisible('app') && (
                                        <td className="db-td db-td--app" data-qh="prop-app">
                                            {meta.appPill && (
                                                <span className="db-app-pill" style={{ background: meta.appPill.bg, color: meta.appPill.color }}>{node.created_by_app_name}</span>
                                            )}
                                        </td>
                                    )}
                                    {isColVisible('schema') && (
                                        <td className="db-td db-td--schema" data-qh="prop-schema">
                                            {meta.schemaName && (
                                                <span className="db-schema-pill" style={{ background: meta.schemaPill.bg, color: meta.schemaPill.color }}>
                                                    {meta.schemaIcon && <SchemaIcon iconName={meta.schemaIcon} size={11} />}
                                                    {meta.schemaName}
                                                    {meta.isValidated && <ShieldCheck size={11} className="schema-validated-icon" title="Validated Schema" />}
                                                </span>
                                            )}
                                        </td>
                                    )}
                                    {isColVisible('relations') && (
                                        <td className="db-td db-td--relations" data-qh="prop-relations">
                                            {(() => {
                                                const types = relsByNode.get(node.id);
                                                if (!types || types.size === 0) return null;
                                                const list = [...types];
                                                return (
                                                    <>
                                                        {list.slice(0, 2).map(type => {
                                                            const pill = getPillColor(type);
                                                            return <span key={type} className="db-rel-pill" style={{ background: pill.bg, color: pill.color }}>{type}</span>;
                                                        })}
                                                        {list.length > 2 && <span className="db-rel-overflow">+{list.length - 2}</span>}
                                                    </>
                                                );
                                            })()}
                                        </td>
                                    )}
                                    {isColVisible('stratum') && (
                                        <td className="db-td db-td--stratum" data-qh="prop-stratum">
                                            {node.stratum && <span className={`db-stratum-pill db-stratum-pill--${node.stratum}`}>{node.stratum}</span>}
                                        </td>
                                    )}
                                    {isColVisible('updated_at') && <td className="db-td db-td--date" data-qh="prop-updated">{fmtDateRelative(node.updated_at)}</td>}
                                    {isColVisible('created_at') && <td className="db-td db-td--date" data-qh="prop-created">{fmtDateRelative(node.created_at)}</td>}
                                </tr>
                                );
                            })}
                            {/* New-page row — always present, Notion-style invite to add.
                                Hidden in catalog mode: this preview has no create path,
                                and a clickable row that silently does nothing is worse
                                than no row at all. */}
                            {!catalogMode && (
                                <tr className="db-row db-row--new" data-qh="row-new" onClick={() => onCreateNode?.()}>
                                    <td className="db-td db-td--new" colSpan={visibleColumns.length}>
                                        <span className="db-new-row">
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                                            New
                                        </span>
                                    </td>
                                </tr>
                            )}
                            {/* Empty database → a few blank rows inviting you to fill it in */}
                            {nodes.length === 0 && [0, 1, 2, 3].map(i => (
                                <tr key={`ph-${i}`} className="db-row db-row--placeholder">
                                    {visibleColumns.map(col => <td key={col.key} className="db-td db-td--placeholder" />)}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Footer — pagination (left) + node count (bottom-right). */}
            {(
            <div className="db-footer">
                {totalPages > 1 && (
                    <div className="db-pagination" data-qh="table-pagination">
                        <button className="btn db-page-btn db-page-btn--circle" onClick={() => onPageChange(p => Math.max(0, p - 1))} disabled={page === 0} aria-label="Previous page"><ChevronLeft size={16} strokeWidth={2.5} /></button>
                        {totalPages <= 8 ? (
                            <div className="db-page-dots" role="tablist" aria-label="Pages">
                                {Array.from({ length: totalPages }, (_, i) => (
                                    <button
                                        key={i}
                                        className={`btn db-page-dot${i === page ? ' db-page-dot--active' : ''}`}
                                        onClick={() => onPageChange(i)}
                                        aria-label={`Page ${i + 1}`}
                                        aria-current={i === page ? 'page' : undefined}
                                    />
                                ))}
                            </div>
                        ) : (
                            <span className="db-page-label"><strong>{page + 1}</strong> / {totalPages}</span>
                        )}
                        <button className="btn db-page-btn db-page-btn--circle" onClick={() => onPageChange(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} aria-label="Next page"><ChevronRight size={16} strokeWidth={2.5} /></button>
                    </div>
                )}
                <span className="db-count" data-qh="table-count"><strong style={{ fontWeight: 700 }}>{total}</strong> nodes</span>
            </div>
            )}

            {/* Settings — right-side config panel for the active database (a saved
                view, or the master library when none is active). */}
            {settingsOpen && (
                <div className="db-edit-panel" ref={editPanelRef}>
                    <div className="db-edit-panel__header">
                        <span className="db-edit-panel__title">Settings</span>
                        <button type="button" className="btn db-edit-panel__close" onClick={() => setSettingsOpen(false)} aria-label="Close">&times;</button>
                    </div>
                    {activeView ? (
                        <input
                            className="db-edit-panel__name"
                            data-qh="dbset-name"
                            value={panelName}
                            placeholder="Database name"
                            onChange={e => { setPanelName(e.target.value); if (e.target.value.trim()) renameView(activeView.id, e.target.value); }}
                        />
                    ) : (
                        <div className="db-edit-panel__name db-edit-panel__name--readonly">{dbName}</div>
                    )}
                    <div className="db-edit-panel__label">View type</div>
                    <div className="db-edit-panel__grid" data-qh="dbset-layout">
                        {VIEW_TYPES.map(t => {
                            const live = t.key === 'table';
                            return (
                                <button
                                    key={t.key}
                                    type="button"
                                    disabled={!live}
                                    className={`btn db-view-type ${live ? 'db-view-type--live db-view-type--selected' : 'db-view-type--soon'}`}
                                    title={live ? 'Table' : 'Coming soon'}
                                >
                                    <span className="db-view-type__icon">{t.icon}</span>
                                    <span className="db-view-type__label">{t.label}</span>
                                    {!live && <span className="db-view-type__soon">soon</span>}
                                </button>
                            );
                        })}
                    </div>
                    <div className="db-edit-panel__label">Layout</div>
                    <button
                        type="button"
                        className="btn db-edit-panel__toggle-row"
                        data-qh="dbset-wrap"
                        onClick={() => setWrapText(!wrapText)}
                        role="switch"
                        aria-checked={wrapText}
                    >
                        <span className="db-edit-panel__toggle-text">
                            <span className="db-edit-panel__toggle-title">Wrap text</span>
                            <span className="db-edit-panel__toggle-sub">Expand rows to show full cell content</span>
                        </span>
                        <SwitchFace on={wrapText} />
                    </button>
                    <div className="db-edit-panel__label">Properties</div>
                    <div className="db-edit-panel__props">
                        {columns.filter(c => c.key !== 'title').map(c => {
                            const hidden = hiddenCols.includes(c.key);
                            return (
                                <button
                                    key={c.key}
                                    type="button"
                                    className={`btn db-edit-panel__prop ${hidden ? 'db-edit-panel__prop--hidden' : ''}`}
                                    data-qh="dbset-prop"
                                    onClick={() => setHiddenCols(hidden ? hiddenCols.filter(k => k !== c.key) : [...hiddenCols, c.key])}
                                >
                                    <span className="db-edit-panel__prop-icon">{c.icon}</span>
                                    <span className="db-edit-panel__prop-label">{c.label}</span>
                                    {hidden ? (
                                        <svg className="db-eye db-eye--off" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M9.9 5.1A9.6 9.6 0 0 1 12 5c6.5 0 10 7 10 7a13.2 13.2 0 0 1-2.2 3M6.3 6.3A13.3 13.3 0 0 0 2 12s3.5 7 10 7a9.5 9.5 0 0 0 4-.9M3 3l18 18M10 10a2.8 2.8 0 0 0 4 4"/></svg>
                                    ) : (
                                        <svg className="db-eye" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/></svg>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                    {activeView && (() => {
                        const isChild = !!activeView.settings?.parentDatabaseId;
                        return (
                            <button
                                type="button"
                                className="btn db-edit-panel__delete"
                                data-qh="dbset-delete"
                                onClick={() => { const id = activeView.id; setSettingsOpen(false); applyView(isChild ? activeDatabase : null); deleteView(id); }}
                            >
                                {isChild ? 'Delete view' : 'Delete database'}
                            </button>
                        );
                    })()}
                </div>
            )}

        </div>
    );
}

export default DatabaseTableView;
